import { c as _c } from "react/compiler-runtime";
import type { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader } from '@/components/DataTable/Columns/DataTableColumnHeader';
import { Text } from '@/components/Typography/Text';
import { type Preset, useFormatDate } from '@/hooks/date';
import { cn } from '@/lib/cn';
import { isDate } from '@/utils/typeGuards';
import type { HelperOptions } from '../types';
type NestedKeys<T> = T extends object ? { [K in keyof T]: K extends string ? `${K}` | `${K}.${NestedKeys<T[K]>}` : never }[keyof T] : never;
function SimpleDateCell(t0) {
  "use memo";

  const $ = _c(14);
  const {
    value,
    rowDepth,
    preset,
    useDefaultText
  } = t0;
  const formatDate = useFormatDate();
  const isSubRow = rowDepth > 0;
  let T0;
  let t1;
  let t2;
  if ($[0] !== formatDate || $[1] !== isSubRow || $[2] !== preset || $[3] !== useDefaultText || $[4] !== value) {
    const date = value instanceof Date ? value : typeof value === "string" || typeof value === "number" ? new Date(value) : null;
    const isValidDate = isDate(date);
    T0 = Text;
    const t3 = useDefaultText ? null : isSubRow ? "text-xs text-neutral-muted-on-fill" : "text-sm text-neutral-on-fill";
    if ($[8] !== t3) {
      t1 = cn(t3);
      $[8] = t3;
      $[9] = t1;
    } else {
      t1 = $[9];
    }
    t2 = isValidDate ? formatDate(date, {
      preset
    }) : "\u2013";
    $[0] = formatDate;
    $[1] = isSubRow;
    $[2] = preset;
    $[3] = useDefaultText;
    $[4] = value;
    $[5] = T0;
    $[6] = t1;
    $[7] = t2;
  } else {
    T0 = $[5];
    t1 = $[6];
    t2 = $[7];
  }
  let t3;
  if ($[10] !== T0 || $[11] !== t1 || $[12] !== t2) {
    t3 = <T0 className={t1}>{t2}</T0>;
    $[10] = T0;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}

/** Date,DateTimeを表示するためのカラム */
export function simpleDateColumn<TData>(id: NestedKeys<TData> & string, preset: keyof Preset = 'date', options: HelperOptions & {
  /** Text が活用されておらず独自のスタイルを当てていたため、デフォルトではその互換性を保つ。true を指定すると通常の Text のスタイルとなる。 */
  useDefaultText?: boolean;
  accessorKey?: string;
}): ColumnDef<TData> {
  const {
    i18nKey = 'createdOn',
    minWidth = '200px',
    sortable = false,
    useDefaultText = false,
    enableHiding = true,
    accessorKey
  } = options;
  return {
    id: id,
    accessorKey: accessorKey ?? id,
    meta: {
      i18nKey,
      thStyle: {
        minWidth
      }
    },
    enableSorting: sortable,
    enableHiding,
    header(props) {
      return <DataTableColumnHeader {...props} />;
    },
    cell: props => {
      const value = props.getValue();
      return <SimpleDateCell value={value} rowDepth={props.row.depth} preset={preset} useDefaultText={useDefaultText} />;
    }
  };
}